import { kebabCase } from 'es-toolkit';

import { _id, _postTitles } from 'src/_mock/assets';

// ----------------------------------------------------------------------

const MOCK_ID = _id[1];
const MOCK_TITLE = _postTitles[2];

const ROOTS = {
  AUTH: '/',
  AUTH_DEMO: '/auth-demo',
  DASHBOARD: '/dashboard',
};

// ----------------------------------------------------------------------

export const paths = {
  comingSoon: '/coming-soon',
  maintenance: '/maintenance',
  pricing: '/pricing',
  payment: '/payment',
  about: '/about-us',
  contact: '/contact-us',
  faqs: '/faqs',
  page403: '/error/403',
  page404: '/error/404',
  page500: '/error/500',
  components: '/components',
  docs: 'https://docs.minimals.cc/',
  changelog: 'https://docs.minimals.cc/changelog/',
  zoneStore: 'https://mui.com/store/items/zone-landing-page/',
  minimalStore: 'https://mui.com/store/items/minimal-dashboard/',
  freeUI: 'https://mui.com/store/items/minimal-dashboard-free/',
  figmaUrl: 'https://www.figma.com/design/WadcoP3CSejUDj7YZc87xj/%5BPreview%5D-Minimal-Web.v7.3.0',
  product: {
    root: `/product`,
    checkout: `/product/checkout`,
    details: (id: string) => `/product/${id}`,
    demo: { details: `/product/${MOCK_ID}` },
  },
  post: {
    root: `/post`,
    details: (title: string) => `/post/${kebabCase(title)}`,
    demo: { details: `/post/${kebabCase(MOCK_TITLE)}` },
  },
  // AUTH (JWT only — other providers removed in Phase 17)
  auth: {
    jwt: {
      signIn: `/`,
      signUp: `/sign-up`,
      forgotPassword: `/forgot-password`,
      resetPassword: `/reset-password`,
    },
  },
  authDemo: {
    split: {
      signIn: `${ROOTS.AUTH_DEMO}/split/sign-in`,
      signUp: `${ROOTS.AUTH_DEMO}/split/sign-up`,
      resetPassword: `${ROOTS.AUTH_DEMO}/split/reset-password`,
      updatePassword: `${ROOTS.AUTH_DEMO}/split/update-password`,
      verify: `${ROOTS.AUTH_DEMO}/split/verify`,
    },
    centered: {
      signIn: `${ROOTS.AUTH_DEMO}/centered/sign-in`,
      signUp: `${ROOTS.AUTH_DEMO}/centered/sign-up`,
      resetPassword: `${ROOTS.AUTH_DEMO}/centered/reset-password`,
      updatePassword: `${ROOTS.AUTH_DEMO}/centered/update-password`,
      verify: `${ROOTS.AUTH_DEMO}/centered/verify`,
    },
  },
  // DASHBOARD
  dashboard: {
    root: ROOTS.DASHBOARD,
    faq: `${ROOTS.DASHBOARD}/faq`,
    mail: `${ROOTS.DASHBOARD}/mail`,
    chat: `${ROOTS.DASHBOARD}/chat`,
    blank: `${ROOTS.DASHBOARD}/blank`,
    kanban: `${ROOTS.DASHBOARD}/kanban`,
    calendar: `${ROOTS.DASHBOARD}/calendar`,
    fileManager: `${ROOTS.DASHBOARD}/file-manager`,
    permission: `${ROOTS.DASHBOARD}/permission`,
    general: {
      app: `${ROOTS.DASHBOARD}/app`,
      ecommerce: `${ROOTS.DASHBOARD}/ecommerce`,
      analytics: `${ROOTS.DASHBOARD}/analytics`,
      banking: `${ROOTS.DASHBOARD}/banking`,
      booking: `${ROOTS.DASHBOARD}/booking`,
      file: `${ROOTS.DASHBOARD}/file`,
      course: `${ROOTS.DASHBOARD}/course`,
    },
    user: {
      root: `${ROOTS.DASHBOARD}/user`,
      new: `${ROOTS.DASHBOARD}/user/new`,
      list: `${ROOTS.DASHBOARD}/user/list`,
      cards: `${ROOTS.DASHBOARD}/user/cards`,
      profile: `${ROOTS.DASHBOARD}/user/profile`,
      account: `${ROOTS.DASHBOARD}/user/account`,
      edit: (id: string) => `${ROOTS.DASHBOARD}/user/${id}/edit`,
      demo: { edit: `${ROOTS.DASHBOARD}/user/${MOCK_ID}/edit` },
    },
    product: {
      root: `${ROOTS.DASHBOARD}/product`,
      new: `${ROOTS.DASHBOARD}/product/new`,
      details: (id: string) => `${ROOTS.DASHBOARD}/product/${id}`,
      edit: (id: string) => `${ROOTS.DASHBOARD}/product/${id}/edit`,
      demo: {
        details: `${ROOTS.DASHBOARD}/product/${MOCK_ID}`,
        edit: `${ROOTS.DASHBOARD}/product/${MOCK_ID}/edit`,
      },
    },
    invoice: {
      root: `${ROOTS.DASHBOARD}/invoice`,
      new: `${ROOTS.DASHBOARD}/invoice/new`,
      details: (id: string) => `${ROOTS.DASHBOARD}/invoice/${id}`,
      edit: (id: string) => `${ROOTS.DASHBOARD}/invoice/${id}/edit`,
      demo: {
        details: `${ROOTS.DASHBOARD}/invoice/${MOCK_ID}`,
        edit: `${ROOTS.DASHBOARD}/invoice/${MOCK_ID}/edit`,
      },
    },
    post: {
      root: `${ROOTS.DASHBOARD}/post`,
      new: `${ROOTS.DASHBOARD}/post/new`,
      details: (title: string) => `${ROOTS.DASHBOARD}/post/${kebabCase(title)}`,
      edit: (title: string) => `${ROOTS.DASHBOARD}/post/${kebabCase(title)}/edit`,
      demo: {
        details: `${ROOTS.DASHBOARD}/post/${kebabCase(MOCK_TITLE)}`,
        edit: `${ROOTS.DASHBOARD}/post/${kebabCase(MOCK_TITLE)}/edit`,
      },
    },
    order: {
      root: `${ROOTS.DASHBOARD}/order`,
      details: (id: string) => `${ROOTS.DASHBOARD}/order/${id}`,
      demo: { details: `${ROOTS.DASHBOARD}/order/${MOCK_ID}` },
    },
    job: {
      root: `${ROOTS.DASHBOARD}/job`,
      new: `${ROOTS.DASHBOARD}/job/new`,
      details: (id: string) => `${ROOTS.DASHBOARD}/job/${id}`,
      edit: (id: string) => `${ROOTS.DASHBOARD}/job/${id}/edit`,
      demo: {
        details: `${ROOTS.DASHBOARD}/job/${MOCK_ID}`,
        edit: `${ROOTS.DASHBOARD}/job/${MOCK_ID}/edit`,
      },
    },
    tour: {
      root: `${ROOTS.DASHBOARD}/tour`,
      new: `${ROOTS.DASHBOARD}/tour/new`,
      details: (id: string) => `${ROOTS.DASHBOARD}/tour/${id}`,
      edit: (id: string) => `${ROOTS.DASHBOARD}/tour/${id}/edit`,
      demo: {
        details: `${ROOTS.DASHBOARD}/tour/${MOCK_ID}`,
        edit: `${ROOTS.DASHBOARD}/tour/${MOCK_ID}/edit`,
      },
    },
    publications: {
      root: `${ROOTS.DASHBOARD}/publications`,
      new: `${ROOTS.DASHBOARD}/publications/new`,
      details: (id: string) => `${ROOTS.DASHBOARD}/publications/${id}`,
      media: (id: string) => `${ROOTS.DASHBOARD}/publications/${id}/media`,
    },
    messages: {
      root: `${ROOTS.DASHBOARD}/messages`,
      thread: (id: string) => `${ROOTS.DASHBOARD}/messages/${id}`,
    },
    appointments: `${ROOTS.DASHBOARD}/appointments`,
    billing: {
      root: `${ROOTS.DASHBOARD}/billing`,
      paymentMethod: `${ROOTS.DASHBOARD}/billing/payment-method`,
      invoices: `${ROOTS.DASHBOARD}/billing/invoices`,
      invoiceDetail: (id: string) => `${ROOTS.DASHBOARD}/billing/invoices/${id}`,
      usage: `${ROOTS.DASHBOARD}/billing/usage`,
    },
    crm: {
      lead: (id: string) => `${ROOTS.DASHBOARD}/crm/${id}`,
      leads: `${ROOTS.DASHBOARD}/crm/leads`,
      root: `${ROOTS.DASHBOARD}/crm`,
    },
    leads: {
      root: `${ROOTS.DASHBOARD}/leads`,
    },
    reports: {
      agents: `${ROOTS.DASHBOARD}/reports/agents`,
      analytics: `${ROOTS.DASHBOARD}/reports/analytics`,
      publications: `${ROOTS.DASHBOARD}/reports/publications`,
      root: `${ROOTS.DASHBOARD}/reports`,
    },
    admin: {
      root: `${ROOTS.DASHBOARD}/admin`,
      moderation: `${ROOTS.DASHBOARD}/admin/moderation`,
      moderationCase: (id: string) => `${ROOTS.DASHBOARD}/admin/moderation/${id}`,
      users: `${ROOTS.DASHBOARD}/admin/users`,
      userDetail: (id: string) => `${ROOTS.DASHBOARD}/admin/users/${id}`,
      organizations: `${ROOTS.DASHBOARD}/admin/organizations`,
      orgDetail: (id: string) => `${ROOTS.DASHBOARD}/admin/organizations/${id}`,
      auditLog: `${ROOTS.DASHBOARD}/admin/audit-log`,
      metrics: `${ROOTS.DASHBOARD}/admin/metrics`,
      subscriptions: `${ROOTS.DASHBOARD}/admin/subscriptions`,
      billingTiers: `${ROOTS.DASHBOARD}/admin/billing/tiers`,
      billingTierPrices: `${ROOTS.DASHBOARD}/admin/billing/tier-prices`,
      billingTaxes: `${ROOTS.DASHBOARD}/admin/billing/taxes`,
      billingInvoices: `${ROOTS.DASHBOARD}/admin/billing/invoices`,
      platformSettings: `${ROOTS.DASHBOARD}/admin/billing/settings`,
    },
    settings: {
      root: `${ROOTS.DASHBOARD}/settings`,
      organization: `${ROOTS.DASHBOARD}/settings/organization`,
      profile: `${ROOTS.DASHBOARD}/settings/profile`,
      whatsapp: `${ROOTS.DASHBOARD}/settings/whatsapp`,
      branding: `${ROOTS.DASHBOARD}/settings/branding`,
    },
    team: {
      root: `${ROOTS.DASHBOARD}/team`,
      agents: `${ROOTS.DASHBOARD}/team/agents`,
      agentDetail: (id: string) => `${ROOTS.DASHBOARD}/team/agents/${id}`,
      invitations: `${ROOTS.DASHBOARD}/team/invitations`,
    },
    campanas: {
      root: `${ROOTS.DASHBOARD}/campanas`,
      lista: `${ROOTS.DASHBOARD}/campanas/lista`,
      nueva: `${ROOTS.DASHBOARD}/campanas/nueva`,
      detail: (id: string) => `${ROOTS.DASHBOARD}/campanas/${id}`,
      plantillas: `${ROOTS.DASHBOARD}/campanas/plantillas`,
    },
    publicaciones: {
      importar: `${ROOTS.DASHBOARD}/publicaciones/importar`,
    },
  },
};
